/**
 * Hash routing (doc 147 §8.3 / §9 Phase 1).
 *
 * Shareable URLs: `#entity/Holding`, `#entity/Holding/field/accountIds`,
 * `#workspace`, `#workspace/Holding`, `#domain/investments`, `#hub/money`,
 * `#settings`, `#` (welcome). Legacy `#workspace/Holding/sheet(/<field>)?`
 * (from the removed sheet overlay) and `#constellation` are mapped to their
 * canonical routes — the former inline in `parseHash` (see the `workspace`
 * branch below), the latter via `legacyRedirect()`. Pure parse/serialize
 * plus a tiny browser binding.
 */

import {
  parseWorkspacePath,
  workspaceToPath,
  type WorkspaceMode,
} from './workspaceMachine.js';
import type { SandboxOpEntry } from './sandboxOps.js';
import type { TrailEntry } from './workflowTrail.js';
import type { ObjectKind } from './objectKinds.js';

export interface SeedProposal {
  kind: 'split-god-entity' | 'add-bridge-tag' | 'break-cycle';
  entityId: string;
  ref: string;
  rationale: string;
}

export type Route =
  | { kind: 'welcome' }
  | { kind: 'entity'; id: string; field?: string }
  | { kind: 'domain'; id: string }
  | { kind: 'hub'; id?: string }
  | { kind: 'feature'; id: string }
  | { kind: 'surface'; id: string }
  | { kind: 'cloud-function'; id: string }
  | { kind: 'event-type'; id: string }
  | { kind: 'constellation'; domainFilter?: string }
  | { kind: 'relationship-map' }
  | { kind: 'impact'; id: string }
  | { kind: 'trace'; from?: string; to?: string }
  | { kind: 'ask'; focus?: string }
  | { kind: 'boards' }
  | { kind: 'diff' }
  | {
    kind: 'sandbox';
    sandboxId?: string;
    seedEntity?: string;
    seedObject?: { kind: 'surface' | 'cloud-function' | 'event-type' | 'firestore-path'; id: string };
    seedProposal?: SeedProposal;
  }
  | { kind: 'review'; sandboxId: string; ownerUid?: string }
  | { kind: 'publish'; sandboxId: string; ownerUid?: string }
  | { kind: 'lint'; focus?: string }
  | { kind: 'simulate'; focus?: string }
  | { kind: 'compliance'; focus?: string }
  | { kind: 'patterns'; focus?: string }
  | { kind: 'refactor'; focus?: string }
  | { kind: 'behavioral'; focus?: string }
  | { kind: 'heatmap'; focus?: string }
  | { kind: 'storyboard'; focus?: string }
  | { kind: 'drift'; focus?: string }
  | { kind: 'testdata'; entityId?: string }
  | { kind: 'compliance-hq'; tab?: string }
  | { kind: 'statemachine'; machineId?: string }
  | { kind: 'dashboard' }
  | { kind: 'event-flow' }
  | { kind: 'pii-tracer'; entityId?: string }
  | { kind: 'live-monitor' }
  | { kind: 'lineage'; domain?: string }
  | { kind: 'settings' }
  | { kind: 'workspace'; mode: WorkspaceMode }
  | { kind: 'domain-map' }
  | { kind: 'domain-map'; domainId: string }
  | { kind: 'domain-map'; domainId: string; nodeKind: 'feature' | 'hub'; nodeId: string }
  | { kind: 'user-journeys' }
  | { kind: 'user-journeys'; tab: string }
  | { kind: 'catalog'; query?: string }
  | { kind: 'catalog-domain'; domainId: string }
  | { kind: 'catalog-graph'; focusDomainId?: string }
  | { kind: 'flag-catalog'; flagId?: string; category?: 'killswitch' | 'experiment' | 'rollout' | 'internal'; tier?: string; pack?: string }
  | { kind: 'new-design-change' }
  | { kind: 'firestore-path'; entityId: string };

/** Upper bound on a decoded route id — real entity/domain ids are short. */
export const MAX_ROUTE_ID_LENGTH = 128;

// Ephemeral store — proposal data that cannot be URL-serialised.
// Set by navigateSandboxWithProposal(), consumed once by App.tsx.
let _pendingSeedProposal: SeedProposal | null = null;

// Ephemeral store — op entry data that cannot be URL-serialised.
// Set by navigateSeedOp(), consumed once via takeSeedOp().
let _pendingSeedOp: SandboxOpEntry | null = null;

// Ephemeral store — sandboxId for navigating to Simulate with a pre-selected overlay.
// Set by navigateSimulateSandbox(), consumed once via takeSimulateSandboxId().
let _pendingSimulateSandboxId: string | undefined;

// Ephemeral store — the last route the app was on before navigating to
// 'ask', used by App.tsx to derive per-screen Ask Atlas context without
// threading extra props through every call site. Not URL-serialised.
let _previousNonAskRoute: Route | null = null;
let _lastRoute: Route | null = null;

/** R1.L1 — character whitelist for decoded route ids. Real entity /
 *  domain / hub / focus ids only use Unicode letters/numbers, plus a
 *  small set of separators (space, dot, underscore, hyphen, colon).
 *  Reject everything else at the parser boundary so a hostile hash
 *  can't carry HTML/script payloads into view rendering. Control
 *  characters (U+0000-U+001F, U+007F) are excluded by virtue of being
 *  absent from the whitelist. */
const SAFE_ID = /^[\p{L}\p{N} ._:-]+$/u;
function isSafeId(s: string): boolean { return SAFE_ID.test(s); }

/** Parse a `location.hash` string into a {@link Route}. Unknown hashes → welcome. */
export function parseHash(hash: string): Route {
  const clean = hash.replace(/^#\/?/, '').trim();
  if (clean === '' || clean === 'welcome') return { kind: 'welcome' };
  if (clean === 'settings') return { kind: 'settings' };
  if (clean === 'constellation' || clean.startsWith('constellation?')) {
    const domainFilter =
      new URLSearchParams(clean.split('?')[1] ?? '').get('domain') ?? undefined;
    return domainFilter && isSafeId(domainFilter)
      ? { kind: 'constellation', domainFilter }
      : { kind: 'constellation' };
  }
  if (clean === 'workspace') return { kind: 'workspace', mode: { kind: 'idle' } };
  if (clean === 'trace') return { kind: 'trace' };
  if (clean === 'ask') return { kind: 'ask' };
  if (clean === 'boards') return { kind: 'boards' };
  if (clean === 'relationship-map') return { kind: 'relationship-map' };
  if (clean === 'diff') return { kind: 'diff' };
  if (clean === 'sandbox') return { kind: 'sandbox' };
  if (clean === 'new-design-change') return { kind: 'new-design-change' };
  if (clean === 'lint') return { kind: 'lint' };
  if (clean === 'simulate') return { kind: 'simulate' };
  if (clean === 'compliance') return { kind: 'compliance' };
  if (clean === 'patterns') return { kind: 'patterns' };
  if (clean === 'refactor') return { kind: 'refactor' };
  if (clean === 'behavioral') return { kind: 'behavioral' };
  if (clean === 'heatmap') return { kind: 'heatmap' };
  if (clean === 'storyboard') return { kind: 'storyboard' };
  if (clean === 'drift') return { kind: 'drift' };
  if (clean === 'testdata') return { kind: 'testdata' };
  if (clean === 'compliance-hq') return { kind: 'compliance-hq' };
  if (clean === 'statemachine') return { kind: 'statemachine' };
  if (clean === 'hub') return { kind: 'hub' };
  if (clean === 'dashboard') return { kind: 'dashboard' };
  if (clean === 'event-flow') return { kind: 'event-flow' };
  if (clean === 'pii-tracer') return { kind: 'pii-tracer' };
  if (clean === 'live-monitor') return { kind: 'live-monitor' };
  if (clean === 'lineage') return { kind: 'lineage' };
  if (clean === 'domain-map') return { kind: 'domain-map' };
  if (clean === 'user-journeys') return { kind: 'user-journeys' };
  if (clean === 'catalog' || clean.startsWith('catalog?')) {
    const qi = clean.indexOf('?q=');
    if (qi !== -1) {
      const q = decodeURIComponent(clean.slice(qi + 3)).trim();
      return q ? { kind: 'catalog', query: q } : { kind: 'catalog' };
    }
    return { kind: 'catalog' };
  }
  if (clean === 'catalog-graph' || clean.startsWith('catalog-graph?')) {
    const focusDomainId =
      new URLSearchParams(clean.split('?')[1] ?? '').get('focus') ?? undefined;
    return focusDomainId && isSafeId(focusDomainId)
      ? { kind: 'catalog-graph', focusDomainId }
      : { kind: 'catalog-graph' };
  }

  if (clean === 'flag-catalog' || clean.startsWith('flag-catalog/') || clean.startsWith('flag-catalog?')) {
    const [pathPart, queryPart] = clean.split('?');
    const segments = pathPart!.split('/');
    const flagId = segments[1] ? decodeURIComponent(segments[1]) : undefined;
    const params = new URLSearchParams(queryPart ?? '');
    const category = params.get('category');
    return {
      kind: 'flag-catalog',
      ...(flagId ? { flagId } : {}),
      ...(category ? { category: category as 'killswitch' | 'experiment' | 'rollout' | 'internal' } : {}),
      ...(params.get('tier') ? { tier: params.get('tier')! } : {}),
      ...(params.get('pack') ? { pack: params.get('pack')! } : {}),
    };
  }

  const slash = clean.indexOf('/');
  if (slash === -1) return { kind: 'welcome' };
  const kind = clean.slice(0, slash);
  const rest = clean.slice(slash + 1);

  const withinBound = (s: string): boolean =>
    s.length > 0 && s.length <= MAX_ROUTE_ID_LENGTH && isSafeId(s);

  if (kind === 'workspace') {
    // Legacy: `#workspace/<id>/sheet` or `#workspace/<id>/sheet/<field>` used
    // to open the now-removed sheet overlay. Redirect straight to the
    // canonical entity route — WorkspaceMode can no longer represent
    // "sheet open" at all, so this has to happen before parseWorkspacePath
    // ever runs, not as a post-parse Route→Route transform.
    const legacySheet = rest.match(/^([^/]+)\/sheet(?:\/(.*))?$/);
    if (legacySheet) {
      const id = decodeURIComponent(legacySheet[1]!).trim();
      if (!withinBound(id)) return { kind: 'workspace', mode: { kind: 'idle' } };
      const fieldRaw = legacySheet[2] !== undefined ? decodeURIComponent(legacySheet[2]).trim() : '';
      return { kind: 'entity', id, ...(fieldRaw && withinBound(fieldRaw) ? { field: fieldRaw } : {}) };
    }
    return { kind: 'workspace', mode: parseWorkspacePath(rest) };
  }

  if (kind === 'catalog-domain') {
    const domainId = decodeURIComponent(rest).trim();
    if (!withinBound(domainId)) return { kind: 'welcome' };
    return { kind: 'catalog-domain', domainId };
  }

  if (kind === 'trace') {
    const [from, to] = rest.split('/').map((s) => decodeURIComponent(s).trim());
    return {
      kind: 'trace',
      ...(from && withinBound(from) ? { from } : {}),
      ...(to && withinBound(to) ? { to } : {}),
    };
  }

  // Sandbox: either `#sandbox/<id>` (existing), `#sandbox/seed/<entityId>` (entity seeding),
  // or `#sandbox/seed/<kind>/<id>` (non-entity object seeding).
  if (kind === 'sandbox') {
    const seedMatch = rest.match(/^seed\/(.+)$/);
    if (seedMatch) {
      const seedRest = seedMatch[1]!;
      const objMatch = seedRest.match(/^(surface|cloud-function|event-type|firestore-path)\/(.+)$/);
      if (objMatch) {
        const seedKind = objMatch[1] as 'surface' | 'cloud-function' | 'event-type' | 'firestore-path';
        const seedId = decodeURIComponent(objMatch[2]!).trim();
        // Firestore paths allow slashes and curly braces in addition to the standard safe chars
        const isSafeForPath = seedKind === 'firestore-path'
          ? seedId.length > 0 && seedId.length <= MAX_ROUTE_ID_LENGTH
          : withinBound(seedId);
        if (isSafeForPath) return { kind: 'sandbox', seedObject: { kind: seedKind, id: seedId } };
        return { kind: 'sandbox' };
      }
      const seed = decodeURIComponent(seedRest).trim();
      if (withinBound(seed)) return { kind: 'sandbox', seedEntity: seed };
      return { kind: 'sandbox' };
    }
    const id = decodeURIComponent(rest).trim();
    if (!withinBound(id)) return { kind: 'sandbox' };
    return { kind: 'sandbox', sandboxId: id };
  }

  if (kind === 'review') {
    const id = decodeURIComponent(rest).trim();
    if (!withinBound(id)) return { kind: 'welcome' };
    return { kind: 'review', sandboxId: id };
  }

  if (kind === 'publish') {
    const id = decodeURIComponent(rest).trim();
    if (!withinBound(id)) return { kind: 'welcome' };
    return { kind: 'publish', sandboxId: id };
  }

  // Entity: either `#entity/<id>` (existing) or `#entity/<id>/field/<name>` (new).
  if (kind === 'entity') {
    const fieldMatch = rest.match(/^([^/]+)\/field\/(.*)$/);
    if (fieldMatch) {
      const id = decodeURIComponent(fieldMatch[1]!).trim();
      if (!withinBound(id)) return { kind: 'welcome' };
      const fieldRaw = decodeURIComponent(fieldMatch[2]!).trim();
      return { kind: 'entity', id, ...(fieldRaw && withinBound(fieldRaw) ? { field: fieldRaw } : {}) };
    }
    const id = decodeURIComponent(rest).trim();
    if (!withinBound(id)) return { kind: 'welcome' };
    return { kind: 'entity', id };
  }

  // Routes that accept an optional `focus/<entityId>` trailing segment.
  const focusMatch = rest.match(/^focus\/(.*)$/);
  if (focusMatch !== null) {
    const focusRaw = decodeURIComponent(focusMatch[1]!).trim();
    const focus = withinBound(focusRaw) ? focusRaw : undefined;
    if (kind === 'lint') return focus ? { kind: 'lint', focus } : { kind: 'lint' };
    if (kind === 'patterns') return focus ? { kind: 'patterns', focus } : { kind: 'patterns' };
    if (kind === 'compliance') return focus ? { kind: 'compliance', focus } : { kind: 'compliance' };
    if (kind === 'refactor') return focus ? { kind: 'refactor', focus } : { kind: 'refactor' };
    if (kind === 'heatmap') return focus ? { kind: 'heatmap', focus } : { kind: 'heatmap' };
    if (kind === 'drift') return focus ? { kind: 'drift', focus } : { kind: 'drift' };
    if (kind === 'simulate') return focus ? { kind: 'simulate', focus } : { kind: 'simulate' };
    if (kind === 'behavioral') return focus ? { kind: 'behavioral', focus } : { kind: 'behavioral' };
    if (kind === 'storyboard') return focus ? { kind: 'storyboard', focus } : { kind: 'storyboard' };
    if (kind === 'ask') return focus ? { kind: 'ask', focus } : { kind: 'ask' };
  }

  if (kind === 'domain-map') {
    const [domainId, nodeKind, nodeId] = rest.split('/');
    if (domainId && (nodeKind === 'feature' || nodeKind === 'hub') && nodeId) {
      return { kind: 'domain-map', domainId, nodeKind, nodeId };
    }
    if (domainId) return { kind: 'domain-map', domainId };
    return { kind: 'domain-map' };
  }
  const VALID_UJ_TABS = ['library', 'flow', 'lifecycle', 'simulate'] as const;
  if (kind === 'user-journeys') {
    const tab = VALID_UJ_TABS.includes(rest as typeof VALID_UJ_TABS[number]) ? rest : undefined;
    return { kind: 'user-journeys', ...(tab ? { tab } : {}) };
  }

  const id = decodeURIComponent(rest).trim();
  if (!withinBound(id)) return { kind: 'welcome' };
  if (kind === 'domain') return { kind: 'domain', id };
  if (kind === 'hub') return { kind: 'hub', id };
  if (kind === 'feature') return { kind: 'feature', id };
  if (kind === 'surface') return { kind: 'surface', id };
  if (kind === 'cloud-function') return { kind: 'cloud-function', id };
  if (kind === 'event-type') return { kind: 'event-type', id };
  if (kind === 'impact') return { kind: 'impact', id };
  if (kind === 'testdata') return { kind: 'testdata', entityId: id };
  if (kind === 'compliance-hq') return withinBound(id) ? { kind: 'compliance-hq', tab: id } : { kind: 'compliance-hq' };
  if (kind === 'statemachine') return withinBound(id) ? { kind: 'statemachine', machineId: id } : { kind: 'statemachine' };
  if (kind === 'pii-tracer') return withinBound(id) ? { kind: 'pii-tracer', entityId: id } : { kind: 'pii-tracer' };
  if (kind === 'firestore-path') return withinBound(id) ? { kind: 'firestore-path', entityId: id } : { kind: 'welcome' };
  if (kind === 'lineage') return withinBound(id) ? { kind: 'lineage', domain: id } : { kind: 'lineage' };
  return { kind: 'welcome' };
}

/** Serialize a {@link Route} back to a hash string (always `#`-prefixed). */
export function routeToHash(route: Route): string {
  switch (route.kind) {
    case 'welcome':
      return '#';
    case 'settings':
      return '#settings';
    case 'constellation':
      return route.domainFilter
        ? `#constellation?domain=${encodeURIComponent(route.domainFilter)}`
        : '#constellation';
    case 'ask':
      return route.focus ? `#ask/focus/${encodeURIComponent(route.focus)}` : '#ask';
    case 'boards':
      return '#boards';
    case 'relationship-map':
      return '#relationship-map';
    case 'diff':
      return '#diff';
    case 'new-design-change':
      return '#new-design-change';
    case 'entity':
      return route.field
        ? `#entity/${encodeURIComponent(route.id)}/field/${encodeURIComponent(route.field)}`
        : `#entity/${encodeURIComponent(route.id)}`;
    case 'domain':
      return `#domain/${encodeURIComponent(route.id)}`;
    case 'hub':
      return route.id ? `#hub/${encodeURIComponent(route.id)}` : '#hub';
    case 'feature':
      return `#feature/${encodeURIComponent(route.id)}`;
    case 'surface':
      return `#surface/${encodeURIComponent(route.id)}`;
    case 'cloud-function':
      return `#cloud-function/${encodeURIComponent(route.id)}`;
    case 'event-type':
      return `#event-type/${encodeURIComponent(route.id)}`;
    case 'impact':
      return `#impact/${encodeURIComponent(route.id)}`;
    case 'trace':
      if (route.from && route.to) {
        return `#trace/${encodeURIComponent(route.from)}/${encodeURIComponent(route.to)}`;
      }
      if (route.from) return `#trace/${encodeURIComponent(route.from)}`;
      return '#trace';
    case 'workspace': {
      const tail = workspaceToPath(route.mode);
      return tail === '' ? '#workspace' : `#workspace/${tail}`;
    }
    case 'sandbox':
      if (route.seedObject) {
        return `#sandbox/seed/${route.seedObject.kind}/${encodeURIComponent(route.seedObject.id)}`;
      }
      if (route.seedEntity) return `#sandbox/seed/${encodeURIComponent(route.seedEntity)}`;
      return route.sandboxId ? `#sandbox/${encodeURIComponent(route.sandboxId)}` : '#sandbox';
    case 'review':
      return `#review/${encodeURIComponent(route.sandboxId)}`;
    case 'publish':
      return `#publish/${encodeURIComponent(route.sandboxId)}`;
    case 'lint':
      return route.focus ? `#lint/focus/${encodeURIComponent(route.focus)}` : '#lint';
    case 'simulate':
      return route.focus ? `#simulate/focus/${encodeURIComponent(route.focus)}` : '#simulate';
    case 'compliance':
      return route.focus ? `#compliance/focus/${encodeURIComponent(route.focus)}` : '#compliance';
    case 'patterns':
      return route.focus ? `#patterns/focus/${encodeURIComponent(route.focus)}` : '#patterns';
    case 'refactor':
      return route.focus ? `#refactor/focus/${encodeURIComponent(route.focus)}` : '#refactor';
    case 'behavioral':
      return route.focus ? `#behavioral/focus/${encodeURIComponent(route.focus)}` : '#behavioral';
    case 'heatmap':
      return route.focus ? `#heatmap/focus/${encodeURIComponent(route.focus)}` : '#heatmap';
    case 'storyboard':
      return route.focus ? `#storyboard/focus/${encodeURIComponent(route.focus)}` : '#storyboard';
    case 'drift':
      return route.focus ? `#drift/focus/${encodeURIComponent(route.focus)}` : '#drift';
    case 'testdata':
      return route.entityId
        ? `#testdata/${encodeURIComponent(route.entityId)}`
        : '#testdata';
    case 'compliance-hq':
      return route.tab ? `#compliance-hq/${encodeURIComponent(route.tab)}` : '#compliance-hq';
    case 'statemachine':
      return route.machineId ? `#statemachine/${encodeURIComponent(route.machineId)}` : '#statemachine';
    case 'dashboard':
      return '#dashboard';
    case 'event-flow':
      return '#event-flow';
    case 'pii-tracer':
      return route.entityId ? `#pii-tracer/${encodeURIComponent(route.entityId)}` : '#pii-tracer';
    case 'live-monitor':
      return '#live-monitor';
    case 'lineage':
      return route.domain ? `#lineage/${encodeURIComponent(route.domain)}` : '#lineage';
    case 'domain-map': {
      const r = route as Extract<Route, { kind: 'domain-map' }>;
      if ('nodeId' in r) return `#domain-map/${r.domainId}/${r.nodeKind}/${r.nodeId}`;
      if ('domainId' in r) return `#domain-map/${r.domainId}`;
      return '#domain-map';
    }
    case 'user-journeys': {
      const r = route as Extract<Route, { kind: 'user-journeys' }>;
      return 'tab' in r ? `#user-journeys/${encodeURIComponent(r.tab)}` : '#user-journeys';
    }
    case 'catalog':
      return route.query ? `#catalog?q=${encodeURIComponent(route.query)}` : '#catalog';
    case 'catalog-domain':
      return `#catalog-domain/${encodeURIComponent(route.domainId)}`;
    case 'catalog-graph':
      return route.focusDomainId
        ? `#catalog-graph?focus=${encodeURIComponent(route.focusDomainId)}`
        : '#catalog-graph';
    case 'flag-catalog': {
      const parts: string[] = [];
      if (route.category) parts.push(`category=${encodeURIComponent(route.category)}`);
      if (route.tier) parts.push(`tier=${encodeURIComponent(route.tier)}`);
      if (route.pack) parts.push(`pack=${encodeURIComponent(route.pack)}`);
      const query = parts.length ? `?${parts.join('&')}` : '';
      return route.flagId ? `#flag-catalog/${encodeURIComponent(route.flagId)}${query}` : `#flag-catalog${query}`;
    }
    case 'firestore-path':
      return `#firestore-path/${encodeURIComponent(route.entityId)}`;
  }
}

/**
 * Map legacy routes (kept for shareable-link compatibility) to their
 * canonical equivalents. Returns null when the route is not legacy.
 *
 *   #constellation → #workspace
 *
 * (The other legacy mapping — old `#workspace/<id>/sheet` links — is
 * handled inline in `parseHash`, not here; see that function's `workspace`
 * branch for why.)
 */
export function legacyRedirect(route: Route): Route | null {
  if (route.kind === 'constellation') {
    return { kind: 'workspace', mode: { kind: 'idle' } };
  }
  return null;
}

/** True when two routes are the same kind + parameters. */
export function routesEqual(a: Route, b: Route): boolean {
  if (a.kind !== b.kind) return false;
  if ('id' in a && 'id' in b) return a.id === b.id;
  if (a.kind === 'trace' && b.kind === 'trace') {
    return a.from === b.from && a.to === b.to;
  }
  if (a.kind === 'workspace' && b.kind === 'workspace') {
    if (a.mode.kind !== b.mode.kind) return false;
    if (a.mode.kind === 'idle') return true;
    const bMode = b.mode as Extract<WorkspaceMode, { entityId: string }>;
    return a.mode.entityId === bMode.entityId;
  }
  if (a.kind === 'sandbox' && b.kind === 'sandbox') {
    return a.sandboxId === b.sandboxId;
  }
  if (a.kind === 'review' && b.kind === 'review') {
    return a.sandboxId === b.sandboxId;
  }
  if (a.kind === 'publish' && b.kind === 'publish') {
    return a.sandboxId === b.sandboxId;
  }
  if (a.kind === 'testdata' && b.kind === 'testdata') {
    return a.entityId === b.entityId;
  }
  if (a.kind === 'domain-map' && b.kind === 'domain-map') {
    return (
      ('domainId' in a ? a.domainId : undefined) === ('domainId' in b ? b.domainId : undefined) &&
      ('nodeKind' in a ? a.nodeKind : undefined) === ('nodeKind' in b ? b.nodeKind : undefined) &&
      ('nodeId' in a ? a.nodeId : undefined) === ('nodeId' in b ? b.nodeId : undefined)
    );
  }
  if (a.kind === 'user-journeys' && b.kind === 'user-journeys') {
    return ('tab' in a ? a.tab : undefined) === ('tab' in b ? b.tab : undefined);
  }
  if (a.kind === 'catalog' && b.kind === 'catalog') return a.query === b.query;
  if (a.kind === 'catalog-domain' && b.kind === 'catalog-domain') return a.domainId === b.domainId;
  if (a.kind === 'constellation' && b.kind === 'constellation')
    return a.domainFilter === b.domainFilter;
  if (a.kind === 'catalog-graph' && b.kind === 'catalog-graph')
    return a.focusDomainId === b.focusDomainId;
  if (a.kind === 'flag-catalog' && b.kind === 'flag-catalog') {
    return a.flagId === b.flagId && a.category === b.category && a.tier === b.tier && a.pack === b.pack;
  }
  if (a.kind === 'firestore-path' && b.kind === 'firestore-path') {
    return a.entityId === b.entityId;
  }
  return true;
}

/** Navigate by updating `location.hash` (the popstate/hashchange listener reacts). */
export function navigate(route: Route): void {
  const next = routeToHash(route);
  if (window.location.hash !== next) {
    window.location.hash = next;
  }
}

/** Navigate to an entity's full detail page, optionally with a field selected. */
export function navigateToEntity(entityId: string, field?: string): void {
  navigate({ kind: 'entity', id: entityId, ...(field ? { field } : {}) });
}

/** Navigate to any of the 9 catalogued object kinds from a workflow-trail entry. Thin wrapper — `navigateToObjectKind` is the one place that knows each kind's Route shape. */
export function navigateToObject(entry: TrailEntry): void {
  navigateToObjectKind(entry.kind, entry.id);
}

/** Navigate to any of the 9 catalogued object kinds by their ObjectKind + id — the one navigate call site every relationship UI (ego graph, chips, search, trail, relationship map) routes through. */
export function navigateToObjectKind(kind: ObjectKind, id: string): void {
  switch (kind) {
    case 'entity':
      navigate({ kind: 'entity', id });
      return;
    case 'surface':
      navigate({ kind: 'surface', id });
      return;
    case 'cloud-function':
      navigate({ kind: 'cloud-function', id });
      return;
    case 'event-type':
      navigate({ kind: 'event-type', id });
      return;
    case 'firestore-path':
      navigate({ kind: 'firestore-path', entityId: id });
      return;
    case 'feature':
      navigate({ kind: 'feature', id });
      return;
    case 'flag':
      navigate({ kind: 'flag-catalog', flagId: id });
      return;
    case 'hub':
      navigate({ kind: 'hub', id });
      return;
    case 'statemachine':
      navigate({ kind: 'statemachine', machineId: id });
      return;
  }
}

/** Navigate to sandbox with a pre-seeded proposal (passed in-memory, not in URL). */
export function navigateSandboxWithProposal(
  seedEntity: string,
  proposal: SeedProposal,
): void {
  _pendingSeedProposal = proposal;
  navigate({ kind: 'sandbox', seedEntity });
}

/** Consume and return the pending proposal (clears after first call). */
export function takeSeedProposal(): SeedProposal | null {
  const p = _pendingSeedProposal;
  _pendingSeedProposal = null;
  return p;
}

/**
 * Navigate to the sandbox editor with a pre-seeded op entry (passed in-memory,
 * not the URL — same one-shot pattern as navigateSandboxWithProposal above).
 * Used by the "Ask Atlas to edit this" flow (Task 6) to hand an AI-proposed
 * update-* op straight into the existing AI-proposed-op review UI.
 */
export function navigateSeedOp(entry: SandboxOpEntry): void {
  _pendingSeedOp = entry;
  navigate({ kind: 'sandbox' });
}

/** Consume and return the pending seeded op entry (clears after first call). */
export function takeSeedOp(): SandboxOpEntry | null {
  const entry = _pendingSeedOp;
  _pendingSeedOp = null;
  return entry;
}

/** Navigate to Simulate with a sandbox overlay pre-selected (passed in-memory, not in URL). */
export function navigateSimulateSandbox(sandboxId: string, focus?: string): void {
  _pendingSimulateSandboxId = sandboxId;
  navigate({ kind: 'simulate', ...(focus ? { focus } : {}) });
}

/** Consume and return the pending simulate sandboxId (clears after first call). */
export function takeSimulateSandboxId(): string | undefined {
  const id = _pendingSimulateSandboxId;
  _pendingSimulateSandboxId = undefined;
  return id;
}

/**
 * Bind `hashchange` to a callback, immediately invoking it with the current
 * route. Returns an unsubscribe function.
 */
export function bindHashRouter(onChange: (route: Route) => void): () => void {
  const handler = (): void => {
    const next = parseHash(window.location.hash);
    if (_lastRoute && _lastRoute.kind !== 'ask') {
      _previousNonAskRoute = _lastRoute;
    }
    _lastRoute = next;
    onChange(next);
  };
  window.addEventListener('hashchange', handler);
  handler();
  return () => window.removeEventListener('hashchange', handler);
}

/** The last route visited before navigating to 'ask', or null if none yet (or the app just started on 'ask'). */
export function getPreviousNonAskRoute(): Route | null {
  return _previousNonAskRoute;
}
